import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// App.css만 남기고, 개별 CSS 파일 임포트 제거
import 'styles/Font.css';

// Image imports
import checkedBox from "assets/images/icon/box-checked-icon.svg";
import uncheckedBox from "assets/images/icon/box-unchecked-icon.svg";
import normalChecked from "assets/images/icon/checked-icon.svg";
import normalUnchecked from "assets/images/icon/unchecked-icon.svg";
import RightArrow from 'assets/images/icon/arrow-right-icon.svg';

// Components imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
// import usePreventZoom from "hooks/usePreventZoom";
import TermsModal from "components/ui/Modals/TermsModal/TermsModal";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";

// 약관 항목 데이터
const agreementTerms = [
    { id: "policyTerm", label: "이용약관 동의(필수)", required: true, className: "Terms-Use", modalType: "terms" },
    { id: "privacyTerm", label: "개인정보 수집 동의(필수)", required: true, className: "personal-information", modalType: "privacy" },
    // {id: "membershipTerm", label: "멤버십 및 PT 약관 동의(필수)", required: true, className: "Membership-Terms", modalType: membership},        // 추가예정
    // {id: "marketingTerm", label: "이벤트 및 마케팅 이용약관 동의(선택)", required: false, className: "Marketing-Terms", modalType: marketing},     // 추가예정
];

// 약관 동의 페이지
function Agreement() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    // 동적 높이 측정을 위한 ref 및 state
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // 상태
    const [checkedItems, setCheckedItems] = useState(() => {
        const initialState = { checkAll: false };
        agreementTerms.forEach((term) => {
            initialState[term.id] = false;
        });
        return initialState;
    });

    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState('');

    // 헤더 높이 측정 로직
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 로드 시 측정
        measureHeight();

        // 레이아웃 시프트 대비 (폰트 로딩 등)
        setTimeout(measureHeight, 100);

        // 리사이즈 이벤트 감지
        window.addEventListener('resize', measureHeight);

        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // 핸들러
    const toggleCheckbox = (id) => {
        setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // 전체 동의 핸들러
    const handleCheckAll = () => {
        const nextCheckAll = !checkedItems.checkAll;
        const nextState = { checkAll: nextCheckAll };
        agreementTerms.forEach((term) => {
            nextState[term.id] = nextCheckAll;
        });
        setCheckedItems(nextState);
    };

    // 파생 상태 (버튼 활성화)
    const isButtonActive = agreementTerms
        .filter(term => term.required)
        .every(term => checkedItems[term.id]);

    // Effect: 개별 항목 변경 시 전체 동의 체크박스 상태 동기화
    useEffect(() => {
        const allActiveChecked = agreementTerms.every(term => checkedItems[term.id]);
        if (checkedItems.checkAll !== allActiveChecked) {
            setCheckedItems(prev => ({ ...prev, checkAll: allActiveChecked }));
        }
    }, [checkedItems]);

    // 네비게이션
    const navigate = useNavigate();
    const handleNext = () => {
        const agreementData = { agreementMarketing: checkedItems.marketingTerm };
        sessionStorage.setItem('tempSignUpData', JSON.stringify(agreementData));
        navigate('/verification');
    };

    // 모달
    const openModal = (type) => {
        if (!type) return;
        setModalType(type);
        setModalShow(true);
    };
    const closeModal = () => setModalShow(false);

    // 동적 스타일 적용 (paddingTop 계산)
    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div className="container agreement-page-container" style={staticStyles.backgroundWrapper}>

            {/* [수정] ref 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title="약관 동의" />
            </div>

            {/* [수정] 동적 스타일(dynamicMainStyle) 적용 */}
            <main className="content-wrapper" style={dynamicMainStyle}>
                <div className="termInfo" style={staticStyles.termInfo}>
                    <div className="term1-field">
                        <p className="term1" style={staticStyles.term1}>하입하우스<br />이용약관 동의</p>
                    </div>
                    <div className="term2-field">
                        <p className="term2" style={staticStyles.term2}>하입하우스 서비스 시작 및 가입을 위해<br />정보 제공에 동의해주세요</p>
                    </div>
                </div>

                <div className="termCheck-field" style={staticStyles.termCheckField}>
                    {/* 전체 동의 */}
                    <div
                        style={{
                            ...staticStyles.wrapper,
                            ...(checkedItems.checkAll ? staticStyles.wrapperAgreeChecked : {})
                        }}
                        onClick={handleCheckAll}
                    >
                        <img
                            src={checkedItems.checkAll ? checkedBox : uncheckedBox}
                            alt="전체약관 동의 체크박스"
                            style={staticStyles.checkBoxImg}
                        />
                        <label style={staticStyles.agreeLabel1}>전체약관 동의하기</label>
                    </div>
                    {/* 개별 약관 목록 */}
                    <div className="Individual-terms-container" style={staticStyles.individualTermsContainer}>
                        {agreementTerms.map((term) => (
                            <div
                                key={term.id}
                                style={staticStyles.individualTermItem}
                                onClick={() => toggleCheckbox(term.id)}
                            >
                                <img
                                    src={checkedItems[term.id] ? normalChecked : normalUnchecked}
                                    alt={`${term.label} 체크박스`}
                                    style={staticStyles.normalCheckImg}
                                />
                                <label style={staticStyles.agreeLabelIndividual}>{term.label}</label>

                                <div
                                    style={staticStyles.viewDetailsIconWrapper}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(term.modalType);
                                    }}
                                >
                                    <img
                                        src={RightArrow}
                                        alt="약관 보기"
                                        className="view-details-icon"
                                        style={staticStyles.viewDetailsIcon}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <div className="bottom-button-wrapper" style={staticStyles.bottomButtonWrapper}>
                <button
                    style={{
                        ...staticStyles.agreeNextButton,
                        ...(isButtonActive ? staticStyles.agreeNextButtonActive : {})
                    }}
                    onClick={handleNext}
                    disabled={!isButtonActive}
                >
                    <p className="button-label" style={staticStyles.agreeNextButtonLabel}>다음</p>
                </button>
            </div>

            <TermsModal
                show={modalShow}
                type={modalType}
                onClose={closeModal}
            />
        </div>
    );
}

/** @type {React.CSSProperties} */
const staticStyles = {
    // --- 1. 레이아웃 ---
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: {
        flexShrink: 0,
        backgroundColor: 'transparent',
    },
    // --- 2. 스크롤되는 메인 콘텐츠 ---
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        width: '100%',
        maxWidth: '430px',
        margin: '0 auto',

        // 상단 패딩은 동적으로 계산되므로 여기선 0 또는 초기값으로 둠
        paddingTop: '0',

        // 하단 버튼 영역만큼 하단 패딩 (버튼 래퍼 94px)
        paddingBottom: 'calc(env(safe-area-inset-bottom, 24px) + 94px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
    },
    // --- 3. 페이지 제목 ---
    termInfo: {
        width: '100%',
        marginTop: '25px',
    },
    term1: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 700,
        fontSize: 'clamp(24px, 6.4vw, 28px)',
        lineHeight: 'clamp(34px, 9.07vw, 39px)',
        color: '#F5F8F9',
        margin: '0 0 clamp(12px, 3.2vw, 14px) 0',
    },
    term2: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 500,
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        lineHeight: 'clamp(24px, 6.4vw, 28px)',
        color: '#9A9A9A',
        margin: 0,
    },
    // --- 4. 약관 동의 ---
    termCheckField: {
        width: '100%',
        marginTop: 'clamp(32px, 8.53vw, 37px)',
    },
    wrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
        width: '100%',
        height: 'clamp(58px, 15.47vw, 67px)',
        gap: 'clamp(12px, 3.2vw, 14px)',
        padding: '16px',
        boxSizing: 'border-box',
        border: '1px solid transparent',
        borderRadius: '6px',
        backgroundColor: '#1B1931',
        backgroundClip: 'padding-box',
    },
    wrapperAgreeChecked: {
        backgroundImage:
            'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        backgroundOrigin: 'padding-box, border-box',
        backgroundClip: 'padding-box, border-box',
    },
    checkBoxImg: {
        width: 'clamp(24px, 6.4vw, 28px)',
        height: 'clamp(24px, 6.4vw, 28px)',
    },
    agreeLabel1: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 600,
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        color: '#F5F8F9',
    },
    individualTermsContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 'clamp(16px, 4.27vw, 18px)',
        marginTop: 'clamp(20px, 5.33vw, 23px)',
        paddingLeft: '16px',
        boxSizing: 'border-box',
    },
    individualTermItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
        width: '100%',
        gap: 'clamp(8px, 2.13vw, 9px)',
    },
    normalCheckImg: {
        width: 'clamp(20px, 5.33vw, 23px)',
        height: 'clamp(20px, 5.33vw, 23px)',
    },
    agreeLabelIndividual: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 400,
        fontSize: 'clamp(14px, 3.73vw, 16px)',
        color: '#FCFCFA',
    },
    viewDetailsIconWrapper: {
        width: '26px',
        height: '26px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 'auto',
    },
    viewDetailsIcon: {
        width: '7px',
        height: '14px',
    },
    // --- 5. 하단 고정 버튼 ---
    bottomButtonWrapper: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        padding: '16px 24px calc(env(safe-area-inset-bottom, 24px) + 16px)',
        boxSizing: 'border-box',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    agreeNextButton: {
        width: '100%',
        maxWidth: 'none',
        height: 'clamp(56px, 14.93vw, 64px)',
        border: 'none',
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        background: '#3A394A',
        cursor: 'not-allowed',
        margin: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    agreeNextButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    agreeNextButtonLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 700,
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        color: '#FFFFFF',
        margin: 0,
    },
};

export default Agreement;